import React, { useEffect } from 'react'
import DoneAllIcon from '@mui/icons-material/DoneAll'

import { NotificationDetail } from './NotificationDetail'
import { NotificationRow } from './NotificationRow'
import { SOURCES, useNotificationsStore } from './stores/notificationsStore'
import './NotificationsPage.scss'

const SOURCE_LABELS = {
    CRM: 'CRM', DEALS: 'Deals', LEADS: 'Leads',
    EMAIL: 'Email', LISTINGS: 'Listings', SYSTEM: 'System',
}

export const NotificationsPage = () => {
    const {
        rows, loading, loadingMore, hasMore, unreadOnly, source, archived,
        summary, selectedId, fetchList, setFilter, markAllRead,
    } = useNotificationsStore()

    const selected = rows.find((n) => n.id === selectedId) || null

    // The app-level poller keeps `summary` fresh and refetches the list when
    // `latest_at` moves, but it can't know this page just mounted — so pull
    // page 1 on entry rather than showing whatever the bell last loaded.
    useEffect(() => {
        fetchList({ reset: true })
    }, [fetchList])

    const unreadFor = (key) => summary.by_source?.[key] || 0

    return (
        <div className={`notifications-page${selected ? ' has-detail' : ''}`}>
            <aside className="notif-rail">
                <button
                    className={`notif-rail-item${!source && !archived ? ' active' : ''}`}
                    onClick={() => setFilter({ source: null, archived: false })}
                >
                    <span>All</span>
                    {summary.unread_count > 0 && <span className="count">{summary.unread_count}</span>}
                </button>

                <div className="notif-rail-divider" />

                {SOURCES.map((key) => (
                    <button
                        key={key}
                        className={`notif-rail-item${source === key && !archived ? ' active' : ''}`}
                        onClick={() => setFilter({ source: key, archived: false })}
                    >
                        <span>{SOURCE_LABELS[key]}</span>
                        {unreadFor(key) > 0 && <span className="count">{unreadFor(key)}</span>}
                    </button>
                ))}

                <div className="notif-rail-divider" />

                <button
                    className={`notif-rail-item${archived ? ' active' : ''}`}
                    onClick={() => setFilter({ archived: true, source: null })}
                >
                    <span>Archived</span>
                </button>
            </aside>

            <section className="notif-main-panel">
                <header className="notif-toolbar">
                    {archived ? (
                        // Unread/read and mark-all-read are inbox concepts; an
                        // archived row is read by definition, so showing them
                        // here would offer filters that can only return nothing.
                        <div className="notif-tabs"><span className="notif-tab active">Archived</span></div>
                    ) : (
                        <div className="notif-tabs">
                            <button
                                className={`notif-tab${!unreadOnly ? ' active' : ''}`}
                                onClick={() => setFilter({ unreadOnly: false })}
                            >
                                All
                            </button>
                            <button
                                className={`notif-tab${unreadOnly ? ' active' : ''}`}
                                onClick={() => setFilter({ unreadOnly: true })}
                            >
                                Unread
                            </button>
                        </div>
                    )}

                    {!archived && (
                        <button
                            className="notif-mark-all"
                            onClick={markAllRead}
                            disabled={summary.unread_count === 0}
                        >
                            <DoneAllIcon style={{ fontSize: 16 }} /> Mark all read
                        </button>
                    )}
                </header>

                <div className="notif-list">
                    {loading && <div className="notif-empty">Loading…</div>}

                    {!loading && rows.length === 0 && (
                        <div className="notif-empty">
                            {archived ? 'Nothing archived.'
                                : unreadOnly ? 'Nothing unread.'
                                : 'No notifications yet.'}
                        </div>
                    )}

                    {!loading && rows.map((n) => (
                        <NotificationRow key={n.id} notification={n} />
                    ))}

                    {!loading && hasMore && rows.length > 0 && (
                        <button
                            className="notif-load-more"
                            onClick={() => fetchList()}
                            disabled={loadingMore}
                        >
                            {loadingMore ? 'Loading…' : 'Load more'}
                        </button>
                    )}
                </div>
            </section>

            <NotificationDetail notification={selected} />
        </div>
    )
}
